import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { useForm } from "antd/es/form/Form";
import { useUpdateBookingsByIdsV2 } from "hooks/mutations/booking-v2";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { BookingStatus } from "shared/types/enum";
import {
  showCancelBookingConfirm,
  showCompleteBookingConfirm,
} from "./bookingConfirmModals";

interface BatchBookingActionsProps {
  completeDisabled: boolean;
  cancelDisabled: boolean;
  selectedRowKeys: string[];
  onSelectionClear: () => void;
}

const BatchBookingActions = ({
  completeDisabled,
  cancelDisabled,
  selectedRowKeys,
  onSelectionClear,
}: BatchBookingActionsProps) => {
  const [batchCancelForm] = useForm();
  const { mutate: updateBookings } = useUpdateBookingsByIdsV2();
  const emptySelection = selectedRowKeys.length === 0;

  // Bordered chip buttons (prototype .bulk-btn) — green/red text when armed,
  // muted via the disabled state otherwise.
  const bulkButtonStyle = (color: string, disabled: boolean) => ({
    borderRadius: 8,
    color: disabled ? undefined : color,
  });

  const countSuffix = ` (${selectedRowKeys.length} booking${
    selectedRowKeys.length === 1 ? "" : "s"
  })`;

  return (
    <Flex gap={8}>
      <Button
        icon={<CheckOutlined />}
        disabled={completeDisabled || emptySelection}
        style={bulkButtonStyle(
          colors.success,
          completeDisabled || emptySelection
        )}
        onClick={() =>
          showCompleteBookingConfirm({
            messageSuffix: countSuffix,
            onConfirm: () => {
              updateBookings({
                bookingIds: selectedRowKeys,
                newStatus: BookingStatus.Completed,
              });
              onSelectionClear();
            },
          })
        }
      >
        {translate("schedule_status.completed")} ({selectedRowKeys.length})
      </Button>
      <Button
        icon={<CloseOutlined />}
        disabled={cancelDisabled || emptySelection}
        style={bulkButtonStyle(colors.error, cancelDisabled || emptySelection)}
        onClick={() =>
          showCancelBookingConfirm({
            form: batchCancelForm,
            count: selectedRowKeys.length,
            onConfirm: (isRefund) => {
              updateBookings({
                bookingIds: selectedRowKeys,
                newStatus: isRefund
                  ? BookingStatus.Cancelled
                  : BookingStatus.NoShow,
              });
              onSelectionClear();
            },
          })
        }
      >
        {translate("schedule_status.cancelled")} ({selectedRowKeys.length})
      </Button>
    </Flex>
  );
};

export default BatchBookingActions;
